import { Form } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  useLazyGetPositionsForSystemQuery,
  useLazyGetPositionsQuery,
  useUpdatePositionMutation,
} from '@app/api/Positions';
import {
  useChangeUserPhoneNumberMutation,
  useLazyGetUserProfileForEditQuery,
  useNormalizeUserPhotoMutation,
  usePrepareEmailChangeMutation,
  useSetUserPhotoMutation,
  useUpdateUserPasswordMutation,
  useUpdateUserProfileMutation,
} from '@app/api/User/api';
import type { PhoneValidation } from '@app/components/Phone/types';
import { DATE_PICKER_FORMAT, ISO_FORMAT_INDEX } from '@app/constants';
import {
  RIGHTS_IS_CREATE,
  RIGHTS_POSITIONS,
} from '@app/constants/accessRights';
import {
  ERROR,
  FRONT_WARNING,
  INFO,
  SUCCESS,
  WARNING,
} from '@app/constants/alerts';
import { useAccessRights, useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { handleCatchError } from '@app/services';
import {
  selectCountries,
  selectUser,
  setUser,
  useAppDispatch,
  useAppSelector,
} from '@app/store';
import type { NAuth, NPositions, NSmallGlobalEntities } from '@app/types';
import type { NUsers } from '@app/types/Users';

import type { NUserProfile } from '@pages/Users/UserProfile/UserProfile.types';

const { useForm } = Form;

const emptyPhoneError = 'Нет номера телефона';
const emptyUserIdError = 'Идентификатор пользователя не найден';

export const useUserProfile = () => {
  const { userId, database, activeBranchId, activeBranchCountryId } =
    useUserPrint();
  const [isLoader, setLoader] = useState(false);
  const [currentUser, setCurrentUser] = useState<NUsers.ModelProfile>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPositionsModalOpen, setIsPositionsModalOpen] = useState(false);
  const [phoneValidation, setPhoneValidation] = useState<PhoneValidation>({
    isValid: false,
    errorMessage: null,
  });
  const [emailServerError, setEmailServerError] = useState('');
  const [passwordServerError, setPasswordServerError] = useState('');
  const [mainInfoServerError, setMainInfoServerError] = useState('');
  const [newPhoto, setNewPhoto] = useState<string | null>(null);
  const [positions, setPositions] = useState<NSmallGlobalEntities.ModelItem[]>(
    []
  );
  const [currentPhoneNumber, setCurrentPhoneNumber] = useState('');
  const [currentEmail, setCurrentEmail] = useState('');

  const globalUser = useAppSelector(selectUser);
  const countries = useAppSelector(selectCountries);

  const dispatch = useAppDispatch();

  const { showNotification } = useNotification();

  const [modalPositionsForm] = useForm();
  const [mainForm] = useForm();
  const [telForm] = useForm<NUserProfile.PhoneForm>();
  const pendingPhoneRef = useRef<{ Phone: string; CountryId: number } | null>(
    null
  );
  const { hasUserRight: hasUserRightToCreate } = useAccessRights(
    RIGHTS_POSITIONS,
    RIGHTS_IS_CREATE
  );
  const [getPositionsForSystem] = useLazyGetPositionsForSystemQuery();
  const [getPositions] = useLazyGetPositionsQuery();
  const [updatePosition] = useUpdatePositionMutation();
  const [getUserProfileForEdit] = useLazyGetUserProfileForEditQuery();
  const [normalizeUserPhoto] = useNormalizeUserPhotoMutation();
  const [setUserPhoto] = useSetUserPhotoMutation();
  const [updateUserProfile] = useUpdateUserProfileMutation();
  const [changeUserPhoneNumber] = useChangeUserPhoneNumberMutation();
  const [prepareEmailChange] = usePrepareEmailChangeMutation();
  const [updateUserPassword] = useUpdateUserPasswordMutation();

  const getPhoneCode = useCallback(
    (user: NUsers.ModelProfile) => {
      const phoneCountryId = user?.PhoneCountryId || activeBranchCountryId;

      const code = countries.find(
        (country) => country.Id === phoneCountryId
      )?.TelCode;

      return code ? `+${code}` : '';
    },
    [activeBranchCountryId, countries]
  );

  useEffect(() => {
    const fetchData = async () => {
      setLoader(true);

      try {
        const userProfile = await getUserProfileForEdit({
          ActiveBranchId: activeBranchId,
          logs: { isLogged: true, userId, database },
        }).unwrap();

        const { Phone, Email } = userProfile;

        const phoneCode = getPhoneCode(userProfile);

        setCurrentUser(userProfile);
        setCurrentPhoneNumber(Phone || emptyPhoneError);
        setCurrentEmail(Email || 'Нет электронной почты');

        pendingPhoneRef.current = {
          Phone: phoneCode || '',
          CountryId: userProfile.PhoneCountryId || activeBranchCountryId || 1,
        };

        const positionsResponse = await getPositionsForSystem({
          ActiveBranchId: activeBranchId,
        }).unwrap();

        setPositions([
          { Id: 0, IdS: null, Name: 'Без должности' },
          ...positionsResponse,
        ]);
      } catch (error) {
        showNotification(handleCatchError(error), WARNING);
      } finally {
        setLoader(false);
      }
    };

    fetchData();
  }, [
    userId,
    database,
    activeBranchId,
    showNotification,
    telForm,
    activeBranchCountryId,
    getPhoneCode,
    getPositionsForSystem,
    getUserProfileForEdit,
  ]);

  useEffect(() => {
    if (!isLoader && pendingPhoneRef.current) {
      telForm.setFieldValue('Phone', pendingPhoneRef.current);
      pendingPhoneRef.current = null;
    }
  }, [isLoader, telForm]);

  const showModal = () => {
    setIsModalOpen(true);
  };

  const uploadAvatar = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (file) {
      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = async function () {
        if (typeof reader.result === 'string') {
          const base64String = reader.result
            .replace('data:', '')
            .replace(/^.+,/, '');

          if (!currentUser?.Id) {
            showNotification(
              'Не удалось определить идентификатор пользователя',
              FRONT_WARNING
            );
            return;
          }

          try {
            const { NormalizedPhoto } = await normalizeUserPhoto({
              ActiveBranchId: activeBranchId,
              data: {
                InputImage: base64String,
                InputBytes: null,
                NormalizedPhoto: null,
              },
            }).unwrap();

            const result = await setUserPhoto({
              ActiveBranchId: activeBranchId,
              data: {
                UserId: currentUser.Id,
                PhotoBase64: NormalizedPhoto,
              },
            }).unwrap();

            const newGlobalData = {
              ...globalUser,
              photo: result.PhotoBase64,
            };

            setNewPhoto(result.PhotoBase64);
            dispatch(setUser(newGlobalData));

            if (currentUser) {
              setCurrentUser({
                ...currentUser,
                Photo: result.PhotoBase64,
              });
            }

            showNotification('Фото установлено', SUCCESS);
          } catch (error) {
            showNotification(handleCatchError(error), WARNING);
          }
        }
      };
    }
  };

  const deleteAvatar = async () => {
    if (!currentUser?.Id) {
      showNotification(
        'Не удалось определить идентификатор пользователя',
        FRONT_WARNING
      );
      return;
    }

    try {
      const result = await setUserPhoto({
        ActiveBranchId: activeBranchId,
        data: {
          UserId: currentUser.Id,
          PhotoBase64: null,
        },
      }).unwrap();

      const newGlobalData = {
        ...globalUser,
        photo: result.PhotoBase64,
      };

      setNewPhoto(result.PhotoBase64);
      dispatch(setUser(newGlobalData));

      if (currentUser) {
        setCurrentUser({
          ...currentUser,
          Photo: result.PhotoBase64,
        });
      }

      showNotification('Фото удалено', SUCCESS);
    } catch (error) {
      showNotification(handleCatchError(error), WARNING);
    }
  };

  const submitMainForm = async (
    values: NUserProfile.MainFormValues,
    activeBranchId: NAuth.UserPrint['userId']
  ) => {
    const { Gender, NameFirst, NameLast, NameMiddle, Position, Date } = values;
    const newGlobalData = {
      ...globalUser,
      gender: Gender,
    };

    dispatch(setUser(newGlobalData));

    const DateBirth = Date
      ? Date.format(DATE_PICKER_FORMAT[ISO_FORMAT_INDEX])
      : null;

    if (userId && currentUser) {
      const requestModel = {
        ...currentUser,
        Id: userId,
        NameLast,
        NameFirst,
        NameMiddle,
        Gender,
        Email: currentUser.Email,
        Phone: currentUser.Phone,
        PositionId: Position === 0 ? null : Position,
        DateBirth,
        Timezone: currentUser.Timezone,
        ActiveBranch: activeBranchId,
        Photo: currentUser.Photo,
      };

      setCurrentUser(requestModel);

      try {
        await updateUserProfile({
          ActiveBranchId: activeBranchId,
          data: requestModel,
        }).unwrap();

        const updatedUser = {
          ...newGlobalData,
          nameFirst: requestModel.NameFirst,
          nameLast: requestModel.NameLast,
          phone: requestModel.Phone,
        };
        dispatch(setUser(updatedUser));

        showNotification('Основные данные сохранены', SUCCESS);
        setCurrentPhoneNumber(requestModel.Phone || emptyPhoneError);
      } catch (error) {
        showNotification(handleCatchError(error), WARNING);
        setMainInfoServerError(handleCatchError(error));
      }
    }
  };

  const updatePhoneNumber = useCallback(
    async ({ Phone }: NUserProfile.PhoneForm) => {
      const { errorMessage, isValid } = phoneValidation;
      const phoneErrorMessage =
        errorMessage || (!isValid ? 'Обязательное поле' : '');

      if (phoneErrorMessage) {
        telForm.setFields([
          {
            name: 'Phone',
            errors: [phoneErrorMessage],
          },
        ]);
        return;
      }

      try {
        await changeUserPhoneNumber({
          ActiveBranchId: activeBranchId,
          countryId: Phone.CountryId,
          newNumber: Phone.Phone,
        }).unwrap();

        showNotification('Номер телефона изменён', SUCCESS);

        setCurrentPhoneNumber(Phone.Phone);

        dispatch(
          setUser({
            ...globalUser,
            phone: Phone.Phone,
          })
        );
      } catch (error) {
        showNotification(handleCatchError(error), WARNING);
      }
    },
    [
      showNotification,
      activeBranchId,
      dispatch,
      globalUser,
      telForm,
      phoneValidation,
      changeUserPhoneNumber,
    ]
  );

  const updateEmail: NUserProfile.ArgsForEmailInfo['updateEmail'] = async ({
    Email,
  }) => {
    if (!Email) return;

    const description = (
      <>
        На{' '}
        <b>
          <i>{Email}</i>
        </b>{' '}
        выслано письмо со ссылкой для подтверждения смены адреса электронной
        почты. Перейдите по ссылке из письма для подтверждения.
      </>
    );

    try {
      if (!currentUser?.Id)
        return showNotification(emptyUserIdError, FRONT_WARNING);

      await prepareEmailChange({
        ActiveBranchId: activeBranchId,
        data: {
          UserId: currentUser.Id,
          Email,
        },
      }).unwrap();

      showNotification('Подтверждение смены email', INFO, description);
    } catch (error) {
      const rtkError = error as { status?: string; data?: string };
      if (rtkError?.status === 'WARNING') {
        setEmailServerError(rtkError.data || '');
      } else {
        showNotification(handleCatchError(error), ERROR);
      }
    }
  };

  const submitPasswordForm: NUserProfile.SubmitPasswordForm = async (
    { Password },
    form
  ) => {
    try {
      if (!currentUser?.Id)
        return showNotification(emptyUserIdError, FRONT_WARNING);

      await updateUserPassword({
        ActiveBranchId: activeBranchId,
        data: {
          UserId: currentUser.Id,
          Password,
        },
      }).unwrap();

      form.resetFields();
      showNotification('Новый пароль установлен', SUCCESS);
    } catch (error) {
      const rtkError = error as { status?: string; data?: string };

      if (rtkError?.status === 'WARNING') {
        setPasswordServerError(rtkError.data || '');
      } else {
        showNotification(handleCatchError(error), ERROR);
      }
    }
  };

  const addPosition = () => {
    showPositionsModal();
  };

  const showPositionsModal = () => {
    setIsPositionsModalOpen(true);
  };

  const handlePositionsOk = () => {
    modalPositionsForm.submit();
  };

  const handlePositionsCancel = () => {
    modalPositionsForm.resetFields();
    setIsPositionsModalOpen(false);
  };

  const finishPositionsModal: NUserProfile.FinishPositionsModal = async ({
    Name,
    Description,
  }) => {
    try {
      await updatePosition({
        ActiveBranchId: activeBranchId,
        data: {
          Id: 0,
          Name,
          Description,
          IsDeleted: false,
          MigrateId: null,
        },
      }).unwrap();

      showNotification('Должность успешно добавлена', SUCCESS);

      const response = await getPositions({
        ActiveBranchId: activeBranchId,
      }).unwrap();

      const responseWithEmpty: Array<NPositions.ModelPosition> = [
        {
          Id: 0,
          Name: 'Без должности',
          Description: '',
          IsDeleted: false,
          MigrateId: null,
          Roles: null,
        },
        ...response,
      ];

      const positions: NSmallGlobalEntities.ModelItem[] = responseWithEmpty.map(
        (position) => ({
          Id: position.Id,
          IdS: position.MigrateId?.toString() || null,
          Name: position.Name,
        })
      );
      setPositions(positions);

      const newPosition = responseWithEmpty.find(
        (position) => position.Name === Name
      );

      if (newPosition) {
        mainForm.setFieldValue('Position', newPosition.Id);
      }

      setIsPositionsModalOpen(false);
    } catch (error) {
      const message = (error as Error).message || '';
      const cause = ((error as Error).cause as string) || '';
      if (cause === ERROR) {
        showNotification(message, cause);
      } else if (cause === WARNING) {
        setMainInfoServerError(message);
      }
    }
  };

  const handlePhone = (phone: string) =>
    phone.match(/\d/g) ? `+${phone}` : phone;

  return {
    isLoader,
    currentUser,
    isModalOpen,
    showModal,
    uploadAvatar,
    deleteAvatar,
    newPhoto,
    positions,
    submitMainForm,
    currentPhoneNumber,
    updatePhoneNumber,
    currentEmail,
    updateEmail,
    emailServerError,
    setEmailServerError,
    mainInfoServerError,
    setMainInfoServerError,
    submitPasswordForm,
    passwordServerError,
    setPasswordServerError,
    addPosition,
    isPositionsModalOpen,
    handlePositionsOk,
    handlePositionsCancel,
    modalPositionsForm,
    finishPositionsModal,
    mainForm,
    hasUserRightToCreate,
    setIsModalOpen,
    telForm,
    handlePhone,
    setPhoneValidation,
    phoneValidation,
  };
};
